import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { useReadOnly, ReadOnlyBanner } from "../hooks/useReadOnly";
import api from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Trash2, Edit2, X, Regex } from "lucide-react";

function Modal({ open, onClose, title, children, width = 520 }) {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.3)" }} onClick={onClose} />
      <div style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: width, maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.05rem", margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function RegexForm({ initial, onSave, onCancel, departments, userRole }) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [deptId, setDeptId] = useState(initial?.department_id || "");
  const [mode, setMode] = useState(initial?.mode || "keep");
  const [pattern, setPattern] = useState(initial?.pattern || "");
  const [replacement, setReplacement] = useState(initial?.replacement || "");
  const [captureGroup, setCaptureGroup] = useState(initial?.capture_group ?? 0);
  const [testInput, setTestInput] = useState("");
  const [testOutput, setTestOutput] = useState(null);
  const [error, setError] = useState(""); const [saving, setSaving] = useState(false);

  const runTest = async () => {
    if (!pattern || !testInput) { setTestOutput(null); return; }
    // Use inline transform for test so we don't need to save first
    const transform = mode === "keep" ? `regex_keep:${pattern}` : `regex_replace:${pattern}||${replacement}`;
    try {
      const r = await api.post("/regex/preview", { value: testInput, transforms: [transform] });
      setTestOutput(r.data.output);
    } catch (e) {
      setTestOutput(`❌ ${e.response?.data?.detail || "invalid"}`);
    }
  };

  const submit = async () => {
    setError("");
    if (!name.trim()) { setError("Name is required"); return; }
    if (!pattern.trim()) { setError("Pattern is required"); return; }
    setSaving(true);
    try { await onSave({ name, description, department_id: deptId || null, mode, pattern, replacement, capture_group: Number(captureGroup || 0) }); }
    catch (e) { setError(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      {error && <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "0.5rem", padding: "0.5rem 0.75rem", marginBottom: "0.75rem", fontSize: "0.85rem", color: "#991B1B" }}>{error}</div>}
      <div style={{ display: "grid", gridTemplateColumns: userRole === "superadmin" ? "2fr 1fr 1fr" : "2fr 1fr", gap: "0.75rem", marginBottom: "0.75rem" }}>
        <div>
          <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Name *</label>
          <input data-testid="rx-name" className="recon-input" style={{ width: "100%", boxSizing: "border-box" }} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Branch Code Extractor" />
        </div>
        <div>
          <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Mode</label>
          <select data-testid="rx-mode" className="recon-input" style={{ width: "100%", boxSizing: "border-box" }} value={mode} onChange={e => setMode(e.target.value)}>
            <option value="keep">Keep (extract)</option>
            <option value="replace">Replace</option>
          </select>
        </div>
        {userRole === "superadmin" && (
          <div>
            <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Department</label>
            <select className="recon-input" style={{ width: "100%", boxSizing: "border-box" }} value={deptId} onChange={e => setDeptId(e.target.value)}>
              <option value="">— All —</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        )}
      </div>
      <div style={{ marginBottom: "0.75rem" }}>
        <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Description</label>
        <input className="recon-input" style={{ width: "100%", boxSizing: "border-box" }} value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" />
      </div>
      <div style={{ marginBottom: "0.75rem" }}>
        <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Pattern *</label>
        <input data-testid="rx-pattern" className="recon-input" style={{ width: "100%", boxSizing: "border-box", fontFamily: "JetBrains Mono, monospace", fontSize: "0.82rem" }} value={pattern} onChange={e => setPattern(e.target.value)} placeholder={mode === "keep" ? "BRN\\d{4}" : "[^A-Z0-9]"} />
      </div>
      {mode === "replace" && (
        <div style={{ marginBottom: "0.75rem" }}>
          <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>
            Replacement
            <span title="Use $1 $2 ... $99 for capture groups · ${name} for named groups · $& for the whole match · $$ for a literal $. Python-native \1 \2 also accepted."
              style={{ marginLeft: "0.35rem", color: "var(--text-muted)", cursor: "help", fontSize: "0.9em" }}>ⓘ</span>
          </label>
          <input data-testid="rx-replacement" className="recon-input" style={{ width: "100%", boxSizing: "border-box", fontFamily: "JetBrains Mono, monospace", fontSize: "0.82rem" }} value={replacement} onChange={e => setReplacement(e.target.value)} placeholder="e.g. ($1) $2-$3 · ${name} · $& · $$ for literal $" />
        </div>
      )}
      {mode === "keep" && (
        <div style={{ marginBottom: "0.75rem" }}>
          <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>
            Capture Group <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>(0 = full match, 1 = first group, 2 = second, …)</span>
          </label>
          <input data-testid="rx-capture-group" type="number" min="0" max="20"
            className="recon-input" style={{ width: "120px", boxSizing: "border-box" }}
            value={captureGroup} onChange={e => setCaptureGroup(Number(e.target.value))} />
        </div>
      )}
      <div style={{ marginBottom: "1rem", padding: "0.625rem", background: "var(--bg-subtle)", borderRadius: "0.5rem" }}>
        <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: "0.375rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Test</label>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <input data-testid="rx-test-input" className="recon-input" style={{ flex: 1, fontSize: "0.82rem", fontFamily: "JetBrains Mono, monospace" }} value={testInput} onChange={e => setTestInput(e.target.value)} placeholder="type a sample value..." />
          <button data-testid="rx-test-run" onClick={runTest} className="btn-secondary" style={{ fontSize: "0.78rem", padding: "0.25rem 0.625rem" }}>Run</button>
        </div>
        {testOutput !== null && (
          <div data-testid="rx-test-output" style={{ marginTop: "0.5rem", fontSize: "0.82rem", color: "var(--sage)", fontFamily: "JetBrains Mono, monospace", padding: "0.375rem 0.5rem", background: "white", borderRadius: "0.25rem", border: "1px solid var(--sage-border)" }}>
            → {testOutput}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
        <button onClick={onCancel} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Cancel</button>
        <button data-testid="rx-save" onClick={submit} disabled={saving} className="btn-primary" style={{ fontSize: "0.85rem" }}>{saving ? "Saving..." : "Save"}</button>
      </div>
    </div>
  );
}

export default function RegexPatternsPage() {
  const readOnly = useReadOnly();
  const { user } = useAuth();
  const [patterns, setPatterns] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/regex/");
      setPatterns(r.data);
      if (user?.role === "superadmin") {
        const d = await api.get("/admin/departments");
        setDepartments(d.data);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async (body) => {
    if (editing && editing !== "new" && editing.id) await api.put(`/regex/${editing.id}`, body);
    else await api.post("/regex/", body);
    setEditing(null); load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this regex pattern?")) return;
    await api.delete(`/regex/${id}`); load();
  };

  return (
    <Layout>
      <div className="animate-in" data-testid="regex-patterns-page">
        <ReadOnlyBanner what="regex patterns" />
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "1.5rem" }}>
          <div>
            <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.75rem", color: "var(--text-primary)", margin: 0 }}>Regex Patterns</h1>
            <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", marginTop: "0.25rem" }}>
              Reusable regex extractors and replacers for column transforms
            </p>
          </div>
          {!readOnly && (
            <button data-testid="rx-new-btn" className="btn-primary" onClick={() => setEditing("new")} style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.85rem" }}>
              <Plus size={14} /> New Pattern
            </button>
          )}
        </div>
        <div className="recon-card" style={{ padding: 0, overflow: "hidden" }}>
          {loading ? <div style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>Loading...</div>
          : patterns.length === 0 ? (
            <div style={{ padding: "4rem 2rem", textAlign: "center" }}>
              <Regex size={32} style={{ color: "var(--text-muted)", margin: "0 auto 0.75rem", opacity: 0.4 }} />
              <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", margin: 0 }}>
                No regex patterns yet. <button onClick={() => setEditing("new")} style={{ color: "var(--sage)", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>Create one</button>
              </p>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>{["Name", "Mode", "Pattern", "Replacement", "Department", "Actions"].map(h => <th key={h} className="table-header" style={{ textAlign: "left" }}>{h}</th>)}</tr></thead>
              <tbody>
                {patterns.map(p => (
                  <tr key={p.id} className="table-row" data-testid={`rx-row-${p.id}`}>
                    <td className="table-cell" style={{ fontWeight: 500 }}>{p.name}</td>
                    <td className="table-cell">
                      <span style={{ fontSize: "0.72rem", padding: "0.125rem 0.5rem", background: p.mode === "keep" ? "#EFF6FF" : "#FEF3C7", color: p.mode === "keep" ? "#1E40AF" : "#92400E", borderRadius: "9999px", fontWeight: 600 }}>
                        {p.mode}
                      </span>
                    </td>
                    <td className="table-cell" style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "0.78rem", maxWidth: "260px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.pattern}</td>
                    <td className="table-cell" style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "0.78rem", color: "var(--text-muted)" }}>{p.replacement || (p.mode === "replace" ? "(remove)" : "—")}</td>
                    <td className="table-cell" style={{ color: "var(--text-muted)" }}>{p.department_name || "—"}</td>
                    <td className="table-cell">
                      <div style={{ display: "flex", gap: "0.375rem" }}>
                        <button data-testid={`rx-edit-${p.id}`} onClick={() => setEditing(p)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><Edit2 size={14} /></button>
                        <button data-testid={`rx-delete-${p.id}`} onClick={() => del(p.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626" }}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal open={editing !== null} onClose={() => setEditing(null)} title={editing === "new" ? "New Regex Pattern" : `Edit: ${editing?.name || ""}`}>
        {editing !== null && <RegexForm initial={editing === "new" ? null : editing} departments={departments} userRole={user?.role} onSave={save} onCancel={() => setEditing(null)} />}
      </Modal>
    </Layout>
  );
}
